import { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import {
  ActionsMenu,
  Loader,
  NothingFoundAlert,
  PageContentLayout,
  PageHeaderLayout,
  SideMenu,
} from 'melp-design/components';
import { ROUTES } from 'config';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useRouteParams } from 'utils/useIdParam';
import {
  useAdminDeleteSupplier,
  useAdminSupplier,
} from 'store/admin-suppliers';
import { usePredefinedToasts } from 'utils/Toast';
import { useConfirm } from 'store/confirm';
import { ContentReviewPendingChip } from 'containers/admin';

export const AdminSupplierLayout: FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const { id: supplierId } = useRouteParams(ROUTES.admin.suppliers.details);
  const predefinedToasts = usePredefinedToasts();
  const history = useHistory();
  const { confirmation } = useConfirm();

  const { data: supplier, isLoading } = useAdminSupplier(supplierId);

  const { mutate: deleteSupplier, isLoading: isDeleting } =
    useAdminDeleteSupplier();

  if (isLoading) return <Loader />;
  if (!supplier) return <NothingFoundAlert />;

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('menu.suppliers'),
            url: ROUTES.admin.suppliers.list,
          },
          {
            label: supplier.name,
          },
        ]}
        under={<ContentReviewPendingChip liveEntityId={supplierId} />}
        side={
          <ActionsMenu
            items={[
              {
                label: t('actions.delete'),
                onClick: async () => {
                  const { confirmed } = await confirmation(
                    t('common.areYouSureToDelete', {
                      title: supplier.name,
                    }),
                    undefined,
                    'danger',
                  );

                  if (confirmed) {
                    deleteSupplier(
                      { supplierId },
                      {
                        onSuccess: () => {
                          predefinedToasts.success.deleted();
                          history.push(ROUTES.admin.suppliers.list);
                        },
                      },
                    );
                  }
                },
                disabled: isDeleting,
                color: 'error',
              },
            ]}
          />
        }
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'details',
                label: t('common.profile', {
                  defaultValue: 'Profile',
                }),
                url: ROUTES.admin.suppliers.details.replace(':id', supplierId),
                isActive: path === ROUTES.admin.suppliers.details,
              },
              {
                key: 'partner-portal',
                label: t('marketplaceSuppliers.partnerPortal', {
                  defaultValue: 'Partner portal',
                }),
                url: ROUTES.admin.suppliers.partnerPortal.replace(
                  ':id',
                  supplierId,
                ),
                isActive: path === ROUTES.admin.suppliers.partnerPortal,
              },
              {
                key: 'tracking',
                label: t('marketplaceSuppliers.tracking', {
                  defaultValue: 'Tracking',
                }),
                url: ROUTES.admin.suppliers.tracking.replace(':id', supplierId),
                isActive: path === ROUTES.admin.suppliers.tracking,
              },
            ]}
          />
        }
      >
        {children}
      </PageContentLayout>
    </Stack>
  );
};
